import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, BookOpenIcon, CheckCircleIcon, StarIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import {
  getIntakeForms, deleteIntakeForm, updateIntakeForm, getFormLibrary, importLibraryTemplate,
  getFormSets, createFormSet, updateFormSet, deleteFormSet, assignFormSet,
} from '../services/intakeFormService';
import { getClients } from '../services/clientService';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import Modal from '../components/common/Modal';
import IntakeFormBuilder from '../components/features/intakeForms/IntakeFormBuilder';
import AssignFormModal from '../components/features/intakeForms/AssignFormModal';

const CATEGORY_LABEL = { assessment: 'Validated instrument', demographic: 'Demographic', consent: 'Consent' };
const CATEGORY_COLOR = {
  assessment: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  demographic: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  consent: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

// ─── Library Modal ─────────────────────────────────────────────────────────────
function LibraryModal({ isOpen, onClose, existingKeys }) {
  const queryClient = useQueryClient();
  const [imported, setImported] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['intake-form-library'],
    queryFn: getFormLibrary,
    enabled: isOpen,
  });

  const importMutation = useMutation({
    mutationFn: (key) => importLibraryTemplate(key),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: ['intakeForms'] });
      setImported((p) => ({ ...p, [key]: true }));
      toast.success('Template imported');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to import template'),
  });

  const templates = data?.templates ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Template Library" size="lg">
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
        Import pre-built, validated clinical instruments into your practice.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => {
            const alreadyAdded = existingKeys.has(t.key) || imported[t.key];
            return (
              <div key={t.key} className="flex items-start justify-between gap-4 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLOR[t.category] || ''}`}>
                      {CATEGORY_LABEL[t.category] || t.category}
                    </span>
                    {t.isScored && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        Auto-scored · max {t.maxScore}
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100 text-sm">{t.title}</p>
                  {t.description && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">{t.description}</p>}
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">{t.questionCount} questions</p>
                </div>
                {alreadyAdded ? (
                  <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 shrink-0">
                    <CheckCircleIcon className="h-4 w-4" /> Added
                  </div>
                ) : (
                  <button
                    onClick={() => importMutation.mutate(t.key)}
                    disabled={importMutation.isPending}
                    className="btn btn-secondary text-xs py-1.5 px-3 shrink-0 disabled:opacity-60"
                  >
                    {importMutation.isPending && importMutation.variables === t.key ? 'Importing…' : 'Import'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ─── Form Set Builder Modal ────────────────────────────────────────────────────
function FormSetModal({ isOpen, onClose, editing, availableForms }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [isDefault, setIsDefault] = useState(editing?.isDefault ?? false);
  const [selectedIds, setSelectedIds] = useState(() => new Set((editing?.forms ?? []).map((f) => f.id)));

  // Reset when editing changes
  useState(() => {
    setName(editing?.name ?? '');
    setDescription(editing?.description ?? '');
    setIsDefault(editing?.isDefault ?? false);
    setSelectedIds(new Set((editing?.forms ?? []).map((f) => f.id)));
  });

  const saveMutation = useMutation({
    mutationFn: (data) => editing ? updateFormSet(editing.id, data) : createFormSet(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formSets'] });
      toast.success(editing ? 'Form set updated' : 'Form set created');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save form set'),
  });

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    saveMutation.mutate({
      name: name.trim(),
      description: description.trim() || null,
      isDefault,
      formIds: [...selectedIds],
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editing ? 'Edit form set' : 'New form set'} size="lg">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name *</label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. New Client Intake"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Description</label>
          <input
            type="text"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600"
          />
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Default set
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">(auto-assigned to new clients)</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Forms in this set <span className="text-neutral-400">({selectedIds.size} selected)</span>
          </label>
          {availableForms.length === 0 ? (
            <p className="text-sm text-neutral-400 dark:text-neutral-500">No active forms available. Create forms first.</p>
          ) : (
            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg divide-y divide-neutral-100 dark:divide-neutral-700 max-h-64 overflow-y-auto">
              {availableForms.map((form) => (
                <label key={form.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(form.id)}
                    onChange={() => toggle(form.id)}
                    className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600 flex-shrink-0"
                  />
                  <span className="text-sm text-neutral-800 dark:text-neutral-200">{form.title}</span>
                  <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500">{form.schema?.length ?? 0} Qs</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {saveMutation.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create set'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Assign Set Modal ──────────────────────────────────────────────────────────
function AssignSetModal({ isOpen, onClose, formSet }) {
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState('');

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
    enabled: isOpen,
  });
  const clients = clientsData?.clients ?? [];

  const assignMutation = useMutation({
    mutationFn: () => assignFormSet(formSet.id, clientId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['intakeForms'] });
      toast.success(res.message || 'Forms assigned');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to assign'),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Assign "${formSet?.name}"`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          All {formSet?.forms?.length ?? 0} form(s) in this set will be assigned to the selected client.
        </p>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Client *</label>
          <select className="input" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.lastName}, {c.firstName}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => assignMutation.mutate()}
            disabled={!clientId || assignMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {assignMutation.isPending ? 'Assigning…' : 'Assign forms'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function IntakeForms() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('forms'); // 'forms' | 'sets'

  // Forms state
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Sets state
  const [isSetModalOpen, setIsSetModalOpen] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [assignSetTarget, setAssignSetTarget] = useState(null);
  const [deleteSetTarget, setDeleteSetTarget] = useState(null);

  const { data: formsData, isLoading: formsLoading, isError: formsError } = useQuery({
    queryKey: ['intakeForms'],
    queryFn: getIntakeForms,
  });

  const { data: setsData, isLoading: setsLoading, isError: setsError } = useQuery({
    queryKey: ['formSets'],
    queryFn: getFormSets,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateIntakeForm(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['intakeForms'] }),
    onError: () => toast.error('Failed to update form'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteIntakeForm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intakeForms'] });
      setDeleteTarget(null);
      toast.success('Form deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete form'),
  });

  const deleteSetMutation = useMutation({
    mutationFn: (id) => deleteFormSet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formSets'] });
      setDeleteSetTarget(null);
      toast.success('Form set deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete form set'),
  });

  const forms = formsData?.forms ?? [];
  const sets = setsData ?? [];
  const existingKeys = new Set(forms.map((f) => f.formKey).filter(Boolean));
  const activeForms = forms.filter((f) => f.isActive);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Intake Forms</h1>
        <div className="flex gap-2">
          {tab === 'forms' ? (
            <>
              <button onClick={() => setIsLibraryOpen(true)} className="btn btn-secondary flex items-center gap-2">
                <BookOpenIcon className="h-4 w-4" /> Template library
              </button>
              <button
                onClick={() => { setEditingForm(null); setIsBuilderOpen(true); }}
                className="btn btn-primary flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" /> New form
              </button>
            </>
          ) : (
            <button
              onClick={() => { setEditingSet(null); setIsSetModalOpen(true); }}
              className="btn btn-primary flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" /> New set
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-neutral-200 dark:border-neutral-700 mb-6">
        {[{ key: 'forms', label: 'Forms' }, { key: 'sets', label: 'Form Sets' }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
            }`}
          >
            {label}
            {key === 'sets' && sets.length > 0 && (
              <span className="ml-1.5 text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 px-1.5 py-0.5 rounded-full">
                {sets.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Forms tab ── */}
      {tab === 'forms' && (
        <>
          {formsLoading ? (
            <div className="flex justify-center py-20"><Spinner /></div>
          ) : formsError ? (
            <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load intake forms.</div>
          ) : forms.length === 0 ? (
            <EmptyState
              title="No intake forms yet"
              description="Create a form template to send to clients for completion."
              action={<button onClick={() => setIsBuilderOpen(true)} className="btn btn-primary">New form</button>}
            />
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Title</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Questions</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Responses</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                  {forms.map((form) => (
                    <tr key={form.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <Link to={`/intake-forms/${form.id}`} className="font-medium text-primary-600 hover:underline">
                          {form.title}
                        </Link>
                        {form.description && (
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 truncate max-w-xs">{form.description}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-neutral-600 dark:text-neutral-400">{form.schema?.length ?? 0}</td>
                      <td className="px-6 py-4 text-center text-neutral-600 dark:text-neutral-400">{form.responses?.length ?? 0}</td>
                      <td className="px-6 py-4">
                        <Badge label={form.isActive ? 'Active' : 'Inactive'} variant={form.isActive ? 'success' : 'default'} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setAssignTarget(form)} className="text-sm text-primary-600 hover:underline">Assign</button>
                          <button
                            onClick={() => { setEditingForm(form); setIsBuilderOpen(true); }}
                            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleActiveMutation.mutate({ id: form.id, isActive: !form.isActive })}
                            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                          >
                            {form.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => setDeleteTarget(form)} className="text-sm text-danger-500 hover:text-danger-700">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Form Sets tab ── */}
      {tab === 'sets' && (
        <>
          {setsLoading ? (
            <div className="flex justify-center py-20"><Spinner /></div>
          ) : setsError ? (
            <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load form sets.</div>
          ) : sets.length === 0 ? (
            <EmptyState
              title="No form sets yet"
              description="Create a set to bundle multiple forms and assign them to clients in one step."
              action={
                <button onClick={() => { setEditingSet(null); setIsSetModalOpen(true); }} className="btn btn-primary">
                  New form set
                </button>
              }
            />
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[500px]">
                <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Set name</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Forms</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                  {sets.map((set) => (
                    <tr key={set.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {set.isDefault && (
                            <StarSolid className="h-4 w-4 text-amber-400 flex-shrink-0" title="Default set" />
                          )}
                          <span className="font-medium text-neutral-900 dark:text-neutral-100">{set.name}</span>
                        </div>
                        {set.description && (
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 truncate max-w-xs">{set.description}</p>
                        )}
                        {set.forms && set.forms.length > 0 && (
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 truncate max-w-xs">
                            {set.forms.map((f) => f.title).join(' · ')}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-neutral-600 dark:text-neutral-400">
                        {set.forms?.length ?? 0}
                      </td>
                      <td className="px-6 py-4">
                        {set.isDefault ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            <StarIcon className="h-3 w-3" /> Default
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setAssignSetTarget(set)}
                            className="text-sm text-primary-600 hover:underline flex items-center gap-1"
                          >
                            <UserPlusIcon className="h-3.5 w-3.5" /> Assign
                          </button>
                          <button
                            onClick={() => { setEditingSet(set); setIsSetModalOpen(true); }}
                            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteSetTarget(set)}
                            className="text-sm text-danger-500 hover:text-danger-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Modals ── */}
      <LibraryModal isOpen={isLibraryOpen} onClose={() => setIsLibraryOpen(false)} existingKeys={existingKeys} />

      <Modal
        isOpen={isBuilderOpen}
        onClose={() => { setIsBuilderOpen(false); setEditingForm(null); }}
        title={editingForm ? 'Edit form' : 'New intake form'}
        size="xl"
      >
        <IntakeFormBuilder
          existingForm={editingForm}
          onClose={() => { setIsBuilderOpen(false); setEditingForm(null); }}
        />
      </Modal>

      {assignTarget && (
        <AssignFormModal
          formId={assignTarget.id}
          formTitle={assignTarget.title}
          isOpen={Boolean(assignTarget)}
          onClose={() => setAssignTarget(null)}
        />
      )}

      <Modal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete form?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          Delete &ldquo;{deleteTarget?.title}&rdquo;? This cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setDeleteTarget(null)} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => deleteMutation.mutate(deleteTarget?.id)}
            disabled={deleteMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>

      {isSetModalOpen && (
        <FormSetModal
          isOpen={isSetModalOpen}
          onClose={() => { setIsSetModalOpen(false); setEditingSet(null); }}
          editing={editingSet}
          availableForms={activeForms}
        />
      )}

      {assignSetTarget && (
        <AssignSetModal
          isOpen={Boolean(assignSetTarget)}
          onClose={() => setAssignSetTarget(null)}
          formSet={assignSetTarget}
        />
      )}

      <Modal isOpen={Boolean(deleteSetTarget)} onClose={() => setDeleteSetTarget(null)} title="Delete form set?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          Delete &ldquo;{deleteSetTarget?.name}&rdquo;? This will not affect already-assigned forms.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setDeleteSetTarget(null)} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => deleteSetMutation.mutate(deleteSetTarget?.id)}
            disabled={deleteSetMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteSetMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
